import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { callApi } from 'duck/operations'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { apiGetAllFundingRoundsAsAdmin } from 'scenes/Dashboard/data/fundingRounds/api'
import { FundingRound } from 'scenes/Dashboard/data/fundingRounds/types'
import { fetchAllOrganizations, setOneOrganization } from 'scenes/Dashboard/data/organizations/actions'
import {
  apiExtractData,
  apiGetExtraction,
  apiGetOrganizationAsAdmin,
  apiGetOrganizationMatchStatusAsAdmin,
} from 'scenes/Dashboard/data/organizations/api'
import { Extraction, ExtractionStatus, Organization } from 'scenes/Dashboard/data/organizations/types'
import { ActionType, getType } from 'typesafe-actions'
import { format as formatDate } from 'utils-next/dates'

import delay from '@redux-saga/delay-p'

import { addOrganizationModalSaga } from '../components/AddOrganizationModal/duck/operations'
import {
  checkOrganizationMatchStatus,
  dataExtraction,
  getFilteredOrganizations,
  updateCurrentPage,
  updateSearch,
} from './actions'
import { getOrganizationsManagerState } from './selectors'

export function* organizationsManagerSaga() {
  yield all([
    takeLatest(
      [getType(getFilteredOrganizations), getType(updateCurrentPage), getType(updateSearch)],
      getFilteredOrganizationsSaga
    ),
    takeLatest(getType(dataExtraction.request), dataExtractionSaga),
    takeLatest(getType(checkOrganizationMatchStatus.request), getOrganizationMatchStatusSaga),
    takeLatest(getType(checkOrganizationMatchStatus.success), showOrganizationMatchStatusSaga),
    addOrganizationModalSaga(),
  ])
}

function* dataExtractionSaga(action: ActionType<typeof dataExtraction.request>): any {
  const organizationId = action.payload
  const extraction: Extraction = yield callApi(apiExtractData, organizationId)
  const extractionFinalStatus = yield pollExtractionData(extraction.id)
  if (extractionFinalStatus === 'warning') {
    yield showWarningForExtraction()
  }
  if (extractionFinalStatus === 'done' || extractionFinalStatus === 'warning') {
    yield fetchOrganization(action)
    yield put(dataExtraction.success(organizationId))
  } else {
    yield put(dataExtraction.success(organizationId))
  }
}

function* fetchOrganization(action: ActionType<typeof dataExtraction.request>) {
  const organizationId = action.payload
  const organizationData: Organization = yield callApi(apiGetOrganizationAsAdmin, organizationId)
  const fundingRounds: FundingRound[] = yield callApi(apiGetAllFundingRoundsAsAdmin, organizationId)
  const organization = {
    ...organizationData,
    fundingRounds,
  }
  yield put(setOneOrganization(organization))
}

// Fetch data every 20 seconds
function* pollExtractionData(extractionId: number) {
  while (true) {
    yield call(delay, 3000)
    try {
      const extraction: Extraction = yield callApi(apiGetExtraction, extractionId)
      if (extraction.status === ExtractionStatus.done) {
        return 'done'
      } else if (extraction.status === ExtractionStatus.error) {
        return 'warning'
      }
    } catch (error) {
      return 'error'
    }
  }
}

function* showWarningForExtraction() {
  yield put(
    showFlashNotice({
      message: 'Organization extraction has been completed, with warnings',
      autoHide: true,
      variant: FlashNoticeVariant.warning,
    })
  )
}

function* getFilteredOrganizationsSaga() {
  const { currentPage, limit, search } = yield select(getOrganizationsManagerState)
  yield put(fetchAllOrganizations.request({ currentPage, limit, searchQuery: search }))
}

function* getOrganizationMatchStatusSaga({
  payload: organization,
}: ActionType<typeof checkOrganizationMatchStatus.request>): any {
  const result: any = yield callApi(apiGetOrganizationMatchStatusAsAdmin, organization)
  yield put(checkOrganizationMatchStatus.success({ organization, status: result }))
}

function* showOrganizationMatchStatusSaga({
  payload: { organization, status },
}: ActionType<typeof checkOrganizationMatchStatus.success>) {
  const matchReady = status.ready
  let message = `Organization ${organization.name} Match Not Finished`
  if (matchReady) {
    message = `Last Organization ${organization.name} Match Finished`
    if (status.updatedAt) {
      const finishedAt = formatDate(new Date(status.updatedAt), 'yyyy-MM-d, HH:mm')
      message += ` At ${finishedAt}`
    }
  }
  yield put(
    showFlashNotice({
      message,
      autoHide: true,
      variant: matchReady ? FlashNoticeVariant.success : FlashNoticeVariant.warning,
    })
  )
}
